import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import SummaryColumnChart from './SummaryColumnChart';
import type { HealthSystemSummaryRow } from '../../interfaces/Dashboard';
import { dropZeroTotalRows, sortByHealthSystemName } from '../../utils/summary';

interface HealthSystemChartGridProps {
  summaryData: HealthSystemSummaryRow[];
  filterType: string;
}

/**
 * One small column chart per health system, laid out in a responsive grid.
 *
 * Replaces the legacy `DynamicCharts` component, which created amCharts
 * instances imperatively against div ids built from the health-system name — a
 * pattern that re-created every chart on each render and leaked instances when
 * names collided. Each chart here is a plain declarative child instead.
 */
const HealthSystemChartGrid = ({
  summaryData,
  filterType,
}: HealthSystemChartGridProps) => {
  // A health system/EHR/location with a reported total of 0 gets a whole
  // empty panel with nothing in it - drop it rather than show a blank chart.
  const sorted = dropZeroTotalRows(sortByHealthSystemName(summaryData));

  if (sorted.length === 0) {
    return (
      <Grid size={12}>
        <Paper className='respurce-list-paper'>
          <Typography align='center' sx={{ padding: 4 }}>
            No Record Found
          </Typography>
        </Paper>
      </Grid>
    );
  }

  return (
    <>
      {sorted.map((row, index) => (
        <Grid
          key={row.healthSystemName + '-' + index}
          size={{ xs: 12, sm: 6, md: 4 }}
        >
          <Paper className='respurce-list-paper'>
            <Typography
              variant='subtitle2'
              align='center'
              sx={{ paddingTop: 1, fontWeight: 600 }}
            >
              {row.healthSystemName}
            </Typography>
            <SummaryColumnChart
              data={[row]}
              filterType={filterType}
              height={460}
              showBrush={false}
            />
          </Paper>
        </Grid>
      ))}
    </>
  );
};

export default HealthSystemChartGrid;
